import type ApplicationController from '@aha-app/mvc';

export interface SelectableItemControllerInterface {
  actionHandleItemClick?(object: { item: any; event: MouseEvent }): void;
  actionHandleContextMenu?(object: { item: any; event: MouseEvent }): void;
}

export type SelectableItemController = ApplicationController &
  SelectableItemControllerInterface;

type UniqueIdItemType = {
  uniqueId: string;
};

type IdItemType = {
  id: string;
};

export type SelectableItemType = UniqueIdItemType | IdItemType;

/**
 * Base class for handling common behavior for selectable and draggable
 * things. Inherit from this for your own selectable item types.
 * SelectableItems can be selected and then have actions performed by
 * right-clicking on them.
 *
 * A SelectableItem is repsonsible for:
 *
 * - Holding a reference to the item.
 */
class SelectableItem<
  ItemType extends SelectableItemType = SelectableItemType,
  Controller extends SelectableItemController | null = SelectableItemController
> {
  item: ItemType;
  controller: Controller;

  constructor({
    item,
    controller,
  }: {
    item: ItemType;
    controller: Controller;
  }) {
    this.item = item;
    this.controller = controller;
  }

  /**
   * A value used to represent the item managed by this draggable.
   */
  get id(): string {
    const { item } = this;
    if ('uniqueId' in item) {
      return item.uniqueId;
    }

    if ('id' in item) {
      return item.id;
    }

    return 'undefined';
  }

  /**
   * Return a deterministic but unique key for this selectable/draggable item.
   */
  get uniqueKey(): string {
    return `${this.type}:${this.id}`;
  }

  get type() {
    return this.constructor.name;
  }

  onClick(event) {
    if (!this.controller) return;

    if (
      'actionHandleItemClick' in this.controller &&
      this.controller.actionHandleItemClick
    ) {
      this.controller.actionHandleItemClick({ item: this, event });
    }
  }

  onContextMenu(event) {
    if (!this.controller) return;

    if (
      'actionHandleContextMenu' in this.controller &&
      this.controller.actionHandleContextMenu
    ) {
      this.controller.actionHandleContextMenu({ item: this, event });
    }
  }
}

export default SelectableItem;
